import { Product, ProductAbbreviations } from '@/creditflowresearch/domain/Product';

const COMBINED_TAB_ORDER = 999;

export interface ITab {
  id: string,
  name: string,
  productIds: string[],
  order: number,
}

interface ICombinationRule {
  name: string,
  requiredAbbreviations: string[],
}

export const generateTabs = (availableProducts: string[]): ITab[] => {
  const combinedTabs = createCombinedTabs(availableProducts);
  const individualTabs = createIndividualTabs(availableProducts, combinedTabs);

  const allTabs = [...combinedTabs, ...individualTabs];
  return allTabs.sort((a, b) => a.order - b.order);
};

const createIndividualTabs = (availableProducts: string[], combinedTabs: ITab[]): ITab[] => {
  const combinedAbbreviations = combinedTabs
    .flatMap((tab) => tab.productIds)
    .map((productId) => Product.getAbbreviation(productId))
    .filter(Boolean);

  return availableProducts
    .map((productId) => ({
      abbreviation: Product.getAbbreviation(productId),
      productId,
    }))
    .filter(({ abbreviation }) => abbreviation && !combinedAbbreviations.includes(abbreviation))
    .map(({ abbreviation, productId }) => ({
      id: abbreviation!,
      name: abbreviation!,
      productIds: [productId],
      order: getTabOrder(abbreviation!),
    }));
};

const createCombinedTabs = (availableProducts: string[]): ITab[] => {
  const availableAbbreviations: ProductAbbreviations[] = availableProducts
    .map((id) => Product.getAbbreviation(id))
    .filter(Boolean);

  const combinationRules: ICombinationRule[] = [
    {
      name: 'HYC/IGC',
      requiredAbbreviations: ['HYC', 'IGC'],
    },
  ];

  return combinationRules
    .filter((rule) =>
      rule.requiredAbbreviations.every((abbr: ProductAbbreviations) => availableAbbreviations.includes(abbr)),
    )
    .map((rule) => {
      const productIds = rule.requiredAbbreviations.map((abbr) =>
        availableProducts.find((id) => Product.getAbbreviation(id) === abbr)!,
      );

      return {
        id: rule.name,
        name: rule.name,
        productIds,
        order: getTabOrder(rule.name),
      };
    });
};

const getTabOrder = (tabName: string): number => {
  const sortOrder = Product.getSortOrder(tabName);
  return sortOrder !== null ? sortOrder : COMBINED_TAB_ORDER;
};
